import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { GalleryHorizontalEnd, Download, Trash2, Copy, Check, Loader2, Lock, Images, Share2, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [copied, setCopied] = useState('');
  const [portfolio, setPortfolio] = useState({ public: false, token: null });
  const [tab, setTab] = useState('all');

  const kindOf = (it) => it.kind || 'before-after';
  const isVideo = (it) => (it.content_type || '').includes('video') || (it.content_type || '').includes('webm') || kindOf(it) === 'video';
  const TABS = [
    { key: 'all', label: 'All' },
    { key: 'before-after', label: 'Before & After' },
    { key: 'collage', label: 'Collages' },
    { key: 'video', label: 'Videos' },
    { key: 'design', label: 'AI Designs' },
    { key: 'plan', label: 'Plan Designer' },
  ];
  const KIND_LABEL = { 'before-after': 'Before & After', collage: 'Collage', video: 'Video', design: 'AI Design', plan: 'Plan' };
  const filtered = tab === 'all' ? items : items.filter((it) => kindOf(it) === tab);
  const countFor = (k) => (k === 'all' ? items.length : items.filter((it) => kindOf(it) === k).length);

  useEffect(() => {
    api.get('/gallery')
      .then((r) => setItems(r.data))
      .catch((e) => { if (e?.response?.status === 402) setLocked(true); })
      .finally(() => setLoading(false));
    api.get('/gallery/portfolio/status').then((r) => setPortfolio(r.data)).catch(() => {});
  }, []);

  const togglePortfolio = async () => {
    try {
      if (portfolio.public) { await api.delete('/gallery/portfolio/share'); setPortfolio((p) => ({ ...p, public: false })); toast.message('Portfolio link turned off'); }
      else { const { data } = await api.post('/gallery/portfolio/share'); setPortfolio({ public: true, token: data.token }); toast.success('Portfolio link is live'); }
    } catch { toast.error('Could not update sharing'); }
  };
  const portfolioUrl = portfolio.token ? `${window.location.origin}/portfolio/${portfolio.token}` : '';

  const remove = async (id) => {
    if (!window.confirm('Remove this from your gallery?')) return;
    try { await api.delete(`/gallery/${id}`); setItems((p) => p.filter((i) => i.id !== id)); toast.success('Removed from gallery'); }
    catch { toast.error('Could not delete'); }
  };
  const download = (it) => {
    const a = document.createElement('a');
    a.href = `${API}${it.url}`; a.download = `tradesmate-${it.id}.${isVideo(it) ? 'webm' : 'png'}`; a.target = '_blank';
    document.body.appendChild(a); a.click(); a.remove();
  };
  const copyCaption = async (it) => {
    try { await navigator.clipboard.writeText(it.caption || ''); setCopied(it.id); setTimeout(() => setCopied(''), 1600); } catch { toast.error('Copy failed'); }
  };

  if (loading) return <div className="max-w-6xl mx-auto px-4 py-20 text-center text-slate-400 font-mono"><Loader2 className="animate-spin mx-auto mb-3" /> Loading gallery…</div>;

  if (locked) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="gallery-locked">
        <Lock className="mx-auto text-brand mb-4" size={40} />
        <h1 className="font-display font-semibold text-3xl uppercase tracking-tight">A Pro feature</h1>
        <p className="text-slate-600 mt-2">Build a portfolio of your Before &amp; After posts. Start your 14-day free trial to unlock it.</p>
        <a href="/pricing" className="tm-btn inline-flex mt-5">See Pro plans</a>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="gallery-page">
      <Seo title="My Gallery" noindex />
      <Toaster position="top-center" richColors />

      <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">Business · Portfolio</p>
          <h1 className="font-display font-semibold text-3xl sm:text-4xl uppercase tracking-tight flex items-center gap-2">
            <GalleryHorizontalEnd className="text-brand" size={30} /> My Gallery
          </h1>
          <p className="text-slate-600 mt-2">Your saved Before &amp; Afters, AI designs and plans — your portfolio to show new customers.</p>
        </div>
        <Link to="/before-after" className="tm-btn inline-flex" data-testid="gallery-make-btn"><Images size={16} /> Make a new one</Link>
      </header>

      {/* Share portfolio */}
      <div className="tm-card p-4 mb-6 border border-slate-200" data-testid="portfolio-share-box">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display font-bold uppercase tracking-tight flex items-center gap-2"><Share2 size={18} className="text-brand" /> Public portfolio</h2>
            <p className="text-sm text-slate-600">Share a link customers can browse — pop it in your Instagram bio or on a quote.</p>
          </div>
          <button onClick={togglePortfolio} data-testid="portfolio-share-toggle"
            className={`inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wide border px-3 py-2 transition-colors ${portfolio.public ? 'bg-brand text-white border-brand' : 'border-slate-800 hover:bg-slate-900 hover:text-white'}`}>
            <Share2 size={14} /> {portfolio.public ? 'Sharing on' : 'Share portfolio'}
          </button>
        </div>
        {portfolio.public && portfolioUrl && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="portfolio-share-panel">
            <input readOnly value={portfolioUrl} data-testid="portfolio-share-url" onFocus={(e) => e.target.select()}
              className="flex-1 min-w-[200px] text-xs font-mono border border-slate-300 px-2 py-1.5 bg-white" />
            <button onClick={() => { navigator.clipboard.writeText(portfolioUrl); setCopied('pf'); setTimeout(() => setCopied(''), 1600); }}
              data-testid="portfolio-share-copy" className="rounded-full inline-flex items-center gap-1 text-xs uppercase border border-slate-800 px-2 py-1.5 hover:bg-slate-900 hover:text-white font-semibold">{copied === 'pf' ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}</button>
            <a href={portfolioUrl} target="_blank" rel="noreferrer" data-testid="portfolio-share-open" className="rounded-full inline-flex items-center gap-1 text-xs uppercase border border-slate-300 px-2 py-1.5 hover:border-brand hover:text-brand font-semibold"><ExternalLink size={13} /> Open</a>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="tm-card p-12 text-center text-slate-500" data-testid="gallery-empty">
          <GalleryHorizontalEnd size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="font-bold">Your gallery is empty.</p>
          <p className="text-sm mt-1">Save a Before &amp; After, an AI Design or a Plan to build your portfolio.</p>
          <Link to="/before-after" className="tm-btn inline-flex mt-4">Make your first post</Link>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-5 border-b border-slate-200 pb-1" data-testid="gallery-tabs">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`gallery-tab-${t.key}`}
                className={`px-3 py-1.5 text-sm font-mono uppercase tracking-wide border transition-colors ${tab === t.key ? 'bg-slate-900 text-white border-slate-800' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>
                {t.label} <span className={tab === t.key ? 'text-[#FF8A5B]' : 'text-slate-400'}>{countFor(t.key)}</span>
              </button>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div className="tm-card p-10 text-center text-slate-500" data-testid="gallery-tab-empty">
              <p className="font-bold">Nothing here yet.</p>
              <p className="text-sm mt-1">You haven't saved any {TABS.find((t) => t.key === tab)?.label} items yet.</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((it) => (
              <div key={it.id} className="tm-card overflow-hidden" data-testid={`gallery-item-${it.id}`}>
                <div className="relative">
                  {isVideo(it) ? (
                    <video src={`${API}${it.url}`} controls preload="metadata" className="w-full aspect-square object-cover bg-slate-900" data-testid={`gallery-video-${it.id}`} />
                  ) : (
                    <img src={`${API}${it.url}`} alt={KIND_LABEL[kindOf(it)]} loading="lazy" className="w-full aspect-square object-cover bg-slate-100" />
                  )}
                  <span className="absolute top-2 left-2 bg-slate-900/85 text-white text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm font-semibold" data-testid={`gallery-kind-${it.id}`}>{KIND_LABEL[kindOf(it)] || kindOf(it)}</span>
                </div>
                <div className="p-3">
                  {it.caption && <p className="text-xs text-slate-600 line-clamp-3 mb-2 whitespace-pre-wrap">{it.caption}</p>}
                  <div className="flex items-center gap-2">
                    <button onClick={() => download(it)} data-testid={`gallery-download-${it.id}`} className="flex-1 inline-flex items-center justify-center gap-1 text-xs uppercase border border-slate-800 py-1.5 hover:bg-slate-900 hover:text-white transition-colors font-semibold"><Download size={13} /> Save</button>
                    {it.caption && <button onClick={() => copyCaption(it)} data-testid={`gallery-copy-${it.id}`} className="inline-flex items-center gap-1 text-xs uppercase border border-slate-300 py-1.5 px-2 hover:border-brand hover:text-brand font-semibold">{copied === it.id ? <Check size={13} /> : <Copy size={13} />}</button>}
                    <button onClick={() => remove(it.id)} data-testid={`gallery-delete-${it.id}`} className="inline-flex items-center border border-slate-300 py-1.5 px-2 text-slate-500 hover:border-rose-600 hover:text-rose-600"><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </>
      )}
    </div>
  );
}
